import React, { useState, useEffect } from "react";
import { Dialog } from "../../ui/Dialog/Dialog";
import { Button } from "../../ui/Button/Button";
import { Input } from "../../ui/Input/Input";
import { MessageStrip } from "../../ui/MessageStrip/MessageStrip";
import { FormField } from "../../composite/FormField/FormField";
import { ImageUpload } from "../../composite/ImageUpload/ImageUpload";
import { customerApi } from "../../../services/customerService";

/**
 * CustomerEditDialog - Modal for editing an existing customer
 * Only accessible by admins and consultants
 *
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close handler
 * @param {function} onSuccess - Success callback with updated customer
 * @param {object} customer - Customer object to edit
 */
export function CustomerEditDialog({ open, onClose, onSuccess, customer }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    contactEmail: "",
    contactPerson: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [deleteCurrentImage, setDeleteCurrentImage] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  // Initialize form with customer data when dialog opens
  useEffect(() => {
    if (open && customer) {
      setFormData({
        name: customer.name || "",
        description: customer.description || "",
        contactEmail: customer.contactEmail || "",
        contactPerson: customer.contactPerson || "",
      });
      setCurrentImageUrl(customer.imageUrl || "");
      setImageFile(null);
      setDeleteCurrentImage(false);
      setErrors({});
      setApiError("");
    }
  }, [open, customer]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleImageChange = (file) => {
    setImageFile(file);
    setDeleteCurrentImage(false); // If uploading new image, don't delete current one
    if (errors.image) {
      setErrors((prev) => ({ ...prev, image: "" }));
    }
  };

  const handleDeleteCurrentImage = () => {
    setDeleteCurrentImage(true);
    setCurrentImageUrl("");
    setImageFile(null);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Customer name is required";
    } else if (formData.name.length < 2 || formData.name.length > 255) {
      newErrors.name = "Name must be between 2 and 255 characters";
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = "Description must not exceed 1000 characters";
    }

    if (
      formData.contactEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)
    ) {
      newErrors.contactEmail = "Please enter a valid email address";
    }

    if (formData.contactPerson && formData.contactPerson.length > 255) {
      newErrors.contactPerson = "Contact person must not exceed 255 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setApiError("");

    try {
      // Step 1: Update customer details
      const response = await customerApi.updateCustomer(customer.id, formData);

      if (response.data.success) {
        let updatedCustomer = response.data.data.customer;

        // Step 2: Handle image operations
        // If user wants to delete current image
        if (deleteCurrentImage && customer.imageUrl) {
          try {
            await customerApi.deleteCustomerImage(customer.id);
            updatedCustomer.imageUrl = null;
          } catch (imageError) {
            console.error("Error deleting image:", imageError);
          }
        }

        // If user uploaded a new image
        if (imageFile) {
          try {
            const imageResponse = await customerApi.uploadCustomerImage(
              customer.id,
              imageFile
            );

            if (imageResponse.data.success) {
              updatedCustomer.imageUrl =
                imageResponse.data.data.customer.imageUrl;
            }
          } catch (imageError) {
            console.error("Error uploading image:", imageError);
            // Continue even if image upload fails
          }
        }

        // Call success callback
        onSuccess?.(updatedCustomer);
        onClose();
      } else {
        setApiError(response.data.message || "Failed to update customer");
      }
    } catch (error) {
      console.error("Error updating customer:", error);
      setApiError(
        error.response?.data?.message ||
          "An error occurred while updating the customer. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      headerText="Edit Customer"
      onAfterClose={handleClose}
      footer={
        <div className="flex justify-end gap-2 p-4">
          <Button design="Transparent" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button design="Emphasized" onClick={handleSubmit} disabled={loading}>
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      }
    >
      <div className="p-6 space-y-4">
        {apiError && (
          <MessageStrip design="Negative" className="mb-4">
            {apiError}
          </MessageStrip>
        )}

        {/* Profile Image Upload */}
        <ImageUpload
          label="Profile Image (Optional)"
          value={imageFile}
          currentImageUrl={currentImageUrl}
          onChange={handleImageChange}
          onDeleteCurrent={handleDeleteCurrentImage}
          error={errors.image}
        />

        <FormField label="Customer Name" required error={errors.name}>
          <Input
            value={formData.name}
            onInput={(e) => handleChange("name", e.target.value)}
            placeholder="Enter customer name"
            valueState={errors.name ? "Error" : "None"}
          />
        </FormField>

        <FormField
          label="Description"
          error={errors.description}
          helpText="Optional description of the customer"
        >
          <Input
            value={formData.description}
            onInput={(e) => handleChange("description", e.target.value)}
            placeholder="Enter description (optional)"
            valueState={errors.description ? "Error" : "None"}
          />
        </FormField>

        <FormField label="Contact Email" error={errors.contactEmail}>
          <Input
            type="Email"
            value={formData.contactEmail}
            onInput={(e) => handleChange("contactEmail", e.target.value)}
            placeholder="contact@example.com"
            valueState={errors.contactEmail ? "Error" : "None"}
          />
        </FormField>

        <FormField label="Contact Person" error={errors.contactPerson}>
          <Input
            value={formData.contactPerson}
            onInput={(e) => handleChange("contactPerson", e.target.value)}
            placeholder="John Doe"
            valueState={errors.contactPerson ? "Error" : "None"}
          />
        </FormField>
      </div>
    </Dialog>
  );
}
